import React, { useState, useEffect, useCallback } from 'react';
import ListPagination from '../common/ListPagination.js';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { locationHubService } from '../../services/locationHubService';
import companyService from '../../services/CompanyService';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert';
import { Plus, MapPin, Building2, Store, FileSpreadsheet, Download } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import { downloadBlob } from '../../utils/downloadBlob.js';
import { hasInternalHubDispatchControls, canScanQrAttendance } from '../../utils/networkMode.js';
import { downloadHubAttendanceQr } from './downloadHubAttendanceQr.js';
import BulkExcelModal from '../common/BulkExcelModal.js';

const TAB_HUB = 'hub';
const TAB_BRANCH = 'branch';

const hubDispatchTypes = (hub) => {
  if (Array.isArray(hub?.dispatch_types) && hub.dispatch_types.length) return hub.dispatch_types;
  if (hub?.dispatch_types) {
    return String(hub.dispatch_types).split(',').map((v) => v.trim()).filter(Boolean);
  }
  return ['OGCL'];
};

const reserveText = (value, fallback) => {
  const raw = value != null && value !== '' ? value : fallback;
  if (raw == null || raw === '') return '0%';
  return `${raw}%`;
};

const ReserveLines = ({ hub }) => (
  <div className="text-xs leading-4 text-gray-700">
    <div>Loading {reserveText(hub.loading_labour_reserve_percent, hub.labour_reserve_percent)}</div>
    <div>Unloading {reserveText(hub.unloading_labour_reserve_percent, hub.labour_reserve_percent)}</div>
  </div>
);

const HubTypeBadges = ({ hub }) => {
  const ownership = (hub.ownership_type || 'OWN').toUpperCase();
  const types = hubDispatchTypes(hub);
  const perms = [
    hub.allow_paid !== false && 'Paid',
    hub.allow_to_pay !== false && 'To Pay',
    hub.allow_on_account !== false && 'Credit',
    hub.allow_delivery !== false && 'Delivery',
    hub.allow_booking !== false && 'Booking',
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <div className="flex flex-wrap gap-1">
        {types.map((type) => (
          <span
            key={type}
            className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
              type === 'ITCL'
                ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
                : 'bg-sky-50 text-sky-800 border-sky-200'
            }`}
          >
            {type}
          </span>
        ))}
        <span
          className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
            ownership === 'AGENT'
              ? 'bg-amber-50 text-amber-800 border-amber-200'
              : 'bg-emerald-50 text-emerald-800 border-emerald-200'
          }`}
        >
          {ownership === 'AGENT' ? 'Agent' : 'Own'}
        </span>
      </div>
      {perms.length > 0 && (
        <p className="text-[10px] text-gray-500 leading-snug break-words">
          {perms.join(' · ')}
        </p>
      )}
    </div>
  );
};

const LocationHubList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const showReserve = !!user?.labour_reserve_enabled;
  const isSystemAdmin = user?.role === 'System Admin';
  const showDispatchFilters = isSystemAdmin || hasInternalHubDispatchControls(user);
  const [activeTab, setActiveTab] = useState(TAB_BRANCH);
  const [hubs, setHubs] = useState([]);
  const [counts, setCounts] = useState({ hub: 0, branch: 0 });
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [locationFilter, setLocationFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [search, setSearch] = useState('');
  const [dispatchTypeFilter, setDispatchTypeFilter] = useState('');
  const [ownershipFilter, setOwnershipFilter] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const perPage = 10;
  const canDeleteHub = ['System Admin', 'Admin', 'Accountant'].includes(user?.role);
  const canBulkExcel = ['System Admin', 'Admin'].includes(user?.role);
  const attendanceQrExtraItems = (hub) => (
    canScanQrAttendance(user)
      ? [{
          label: 'Download Attendance QR',
          icon: '📱',
          onClick: () => downloadHubAttendanceQr(hub),
        }]
      : []
  );
  const isBranchTab = activeTab === TAB_BRANCH;

  const tabs = [
    {
      id: TAB_BRANCH,
      label: t('pages.locationHub.branchesTab', { defaultValue: 'Branches' }),
      count: counts.branch,
      icon: Store,
      activeClass: 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200',
      hoverClass: 'hover:border-indigo-300',
      iconActive: 'text-indigo-600',
      textActive: 'text-indigo-700',
    },
    {
      id: TAB_HUB,
      label: t('pages.locationHub.hubsTab', { defaultValue: 'Hubs' }),
      count: counts.hub,
      icon: Building2,
      activeClass: 'border-blue-500 bg-blue-50 ring-1 ring-blue-200',
      hoverClass: 'hover:border-blue-300',
      iconActive: 'text-blue-600',
      textActive: 'text-blue-700',
    },
  ];

  const addLabel = isBranchTab
    ? t('pages.locationHub.addBranch', { defaultValue: 'Add Branch' })
    : t('pages.locationHub.addHub', { defaultValue: 'Add Hub' });

  const goToAdd = () => navigate('/location-hubs/add', {
    state: { destinationType: isBranchTab ? 'branch_destination' : 'hub_destination' },
  });

  useEffect(() => {
    if (isSystemAdmin) {
      companyService.getActiveCompanies()
        .then((res) => setCompanies(res.data || []))
        .catch(() => {});
    }
  }, [isSystemAdmin]);

  const buildFilters = useCallback((destinationType) => {
    const filters = { destination_type: destinationType };
    if (search) filters.search = search;
    if (locationFilter) filters.location = locationFilter;
    if (stateFilter) filters.state = stateFilter;
    if (companyFilter) filters.company_id = companyFilter;
    if (showDispatchFilters && dispatchTypeFilter) filters.dispatch_type = dispatchTypeFilter;
    if (showDispatchFilters && ownershipFilter) filters.ownership_type = ownershipFilter;
    return filters;
  }, [search, locationFilter, stateFilter, companyFilter, dispatchTypeFilter, ownershipFilter, showDispatchFilters]);

  const fetchHubs = useCallback(async () => {
    try {
      setLoading(true);
      const filters = buildFilters(isBranchTab ? 'branch_destination' : 'hub_destination');

      const response = await locationHubService.getAllHubs(currentPage, perPage, filters);
      const hubsData = response.data.hubs || response.data.data || [];
      setHubs((prev) => mergeLoadedPage(currentPage, hubsData, prev));
      const pagination = response.data.pagination || {};
      setTotalPages(pagination.pages || 1);
    } catch (error) {
      console.error('Error fetching hubs:', error);
      Alert.error(error.response?.data?.error || t('pages.locationHub.loadFailed'));
      setHubs([]);
    } finally {
      setLoading(false);
    }
  }, [buildFilters, currentPage, perPage, isBranchTab, t]);

  useEffect(() => {
    let cancelled = false;
    const loadCounts = async () => {
      try {
        const [hubRes, branchRes] = await Promise.all([
          locationHubService.getAllHubs(1, 1, buildFilters('hub_destination')),
          locationHubService.getAllHubs(1, 1, buildFilters('branch_destination')),
        ]);
        if (cancelled) return;
        const readTotal = (res) => {
          const pag = res.data.pagination || {};
          if (pag.total !== undefined && pag.total !== null) return pag.total;
          return (res.data.hubs || res.data.data || []).length;
        };
        setCounts({ hub: readTotal(hubRes), branch: readTotal(branchRes) });
      } catch (error) {
        if (!cancelled) setCounts({ hub: 0, branch: 0 });
      }
    };
    loadCounts();
    return () => { cancelled = true; };
  }, [buildFilters]);

  usePaginatedListFetch({
    fetchPage: fetchHubs,
    setCurrentPage,
    currentPage,
    resetPageDeps: [search, locationFilter, stateFilter, companyFilter, dispatchTypeFilter, ownershipFilter, activeTab],
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest('.menu-container')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    setOpenMenuId(null);
    setActiveTab(tabId);
    setCurrentPage(1);
  };

  const handleDelete = async (hubId) => {
    const confirmed = await Alert.confirm(
      t('pages.locationHub.deleteConfirm'),
      t('pages.locationHub.deleteTitle')
    );
    if (!confirmed) return;

    try {
      await locationHubService.deleteHub(hubId);
      Alert.success(t('pages.locationHub.deleteSuccess'));

      if (hubs.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchHubs();
      }
    } catch (error) {
      Alert.error(error.response?.data?.error || t('errors.deleteFailed'));
    }
  };

  const clearFilters = () => {
    setLocationFilter('');
    setStateFilter('');
    setCompanyFilter('');
    setSearch('');
    setDispatchTypeFilter('');
    setOwnershipFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || locationFilter || stateFilter || companyFilter
    || dispatchTypeFilter || ownershipFilter;

  const contactLabel = (hub) => hub.location_hub_contact || '—';

  const emptyTitle = isBranchTab
    ? t('pages.locationHub.noBranches', { defaultValue: 'No branches found' })
    : t('pages.locationHub.noHubs', { defaultValue: 'No hubs found' });

  const emptyHint = isBranchTab
    ? t('pages.locationHub.noBranchesHint', { defaultValue: 'Create a branch and map it to a parent hub.' })
    : t('pages.locationHub.noHubsHint', { defaultValue: 'Create a hub destination to get started.' });

  const downloadFilledExcel = async (request, filename) => {
    try {
      const res = await request();
      await downloadBlob(
        res.data,
        filename,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to export Excel');
    }
  };

  const showingLabel = isBranchTab
    ? t('pages.locationHub.showingBranches', {
        count: hubs.length,
        defaultValue: `Showing ${hubs.length} branch${hubs.length === 1 ? '' : 'es'}`,
      })
    : t('pages.locationHub.showingHubs', { count: hubs.length });

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto">
        <ListPageHeader
          title={t('pages.locationHub.title')}
          icon={MapPin}
          showMobileIcon
          className="mb-4"
        >
          {canBulkExcel ? (
            <>
              <Button mobileIconOnly variant="secondary" icon={Download} onClick={() => downloadFilledExcel(
                () => locationHubService.downloadBulkExport(companyFilter || null),
                'location_hubs_export.xlsx',
              )}>
                Export
              </Button>
              <Button mobileIconOnly variant="secondary" icon={FileSpreadsheet} onClick={() => setBulkOpen(true)}>
                Excel
              </Button>
            </>
          ) : null}
          <Button mobileIconOnly variant="primary" icon={Plus} onClick={goToAdd}>
            {addLabel}
          </Button>
        </ListPageHeader>

        <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
            {isSystemAdmin && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('common.company')}</label>
                <select
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                  className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                >
                  <option value="">{t('common.allCompanies')}</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.company_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('common.search')}</label>
              <SearchSubmitInput
                value={search}
                onSearch={setSearch}
                placeholder={t('pages.locationHub.searchPlaceholder')}
                inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('pages.shipment.destination')}</label>
              <SearchSubmitInput
                value={locationFilter}
                onSearch={setLocationFilter}
                placeholder={t('pages.locationHub.destinationPlaceholder')}
                inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('forms.state')}</label>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
              >
                <option value="">{t('common.allStates')}</option>
                <option value="Mumbai">Mumbai</option>
                <option value="Goa">Goa</option>
              </select>
            </div>
            {showDispatchFilters && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('pages.locationHub.dispatchType', { defaultValue: 'Dispatch Type' })}
                </label>
                <select
                  value={dispatchTypeFilter}
                  onChange={(e) => setDispatchTypeFilter(e.target.value)}
                  className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                >
                  <option value="">{t('common.all', { defaultValue: 'All' })}</option>
                  <option value="OGCL">OGCL</option>
                  <option value="ITCL">ITCL</option>
                </select>
              </div>
            )}
            {showDispatchFilters && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('pages.locationHub.ownership', { defaultValue: 'Ownership' })}
                </label>
                <select
                  value={ownershipFilter}
                  onChange={(e) => setOwnershipFilter(e.target.value)}
                  className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                >
                  <option value="">{t('common.all', { defaultValue: 'All' })}</option>
                  <option value="OWN">Own</option>
                  <option value="AGENT">Agent</option>
                </select>
              </div>
            )}
        </ListFilterPanel>

        {/* Hub / Branch scope tabs — cashbook-style with live counts */}
        <div
          className="grid grid-cols-2 gap-2 sm:gap-3 mb-4"
          role="tablist"
          aria-label="Location hub type"
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-3 rounded-xl border px-3 sm:px-4 py-2.5 text-left transition-colors ${
                  isActive ? tab.activeClass : `border-gray-200 bg-white ${tab.hoverClass}`
                }`}
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isActive ? 'bg-white/70' : 'bg-gray-100'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? tab.iconActive : 'text-gray-400'}`} />
                </span>
                <span className="min-w-0">
                  <span className={`block text-xs sm:text-sm font-semibold ${isActive ? tab.textActive : 'text-gray-600'}`}>
                    {tab.label}
                  </span>
                  <span className={`block text-base sm:text-lg font-bold leading-tight ${isActive ? tab.textActive : 'text-gray-700'}`}>
                    {tab.count}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading && currentPage === 1 ? (
            <div className="flex items-center justify-center py-20">
              <Loader fullScreen={false} size="default" message={t('pages.locationHub.loading')} />
            </div>
          ) : hubs.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="text-6xl mb-4">📍</div>
              <p className="text-gray-600 text-lg font-semibold">{emptyTitle}</p>
              <p className="text-gray-500 mt-2">{emptyHint}</p>
            </div>
          ) : (
            <>
              {/* Desktop + iPad landscape: full table */}
              <div className="hidden lg:block w-full overflow-x-auto">
                <table className="w-full table-fixed border-collapse min-w-[860px]">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700 border-b">
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase tracking-wider w-[18%]">{t('pages.locationHub.hubBranchColumn', { defaultValue: 'Hub/Branch' })}</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase tracking-wider w-[8%]">{t('pages.locationHub.hubCode')}</th>
                      {isSystemAdmin && (
                        <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase tracking-wider w-[12%]">{t('common.company')}</th>
                      )}
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase tracking-wider w-[18%]">{t('common.address')}</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase tracking-wider w-[16%]">
                        {t('pages.locationHub.typePermissions', { defaultValue: 'Type / Permissions' })}
                      </th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase tracking-wider w-[10%]">{t('forms.state')}</th>
                      {showReserve && (
                        <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase tracking-wider w-[12%]">Reserve</th>
                      )}
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase tracking-wider w-[12%]">Contact No</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold uppercase tracking-wider w-[8%]">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {hubs.map((hub, index) => (
                      <tr key={hub.id} className={`hover:bg-gray-50 relative transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-5 py-2 align-top">
                          <span
                            className={listIdLinkClass}
                            onClick={() => navigate(`/location-hubs/view/${hub.hub_id}`)}
                          >
                            {hub.location}
                          </span>
                          {isBranchTab && hub.parent_hub_name ? (
                            <p className="text-xs text-gray-500 mt-0.5 break-words">{hub.parent_hub_name}</p>
                          ) : null}
                        </td>
                        <td className="px-5 py-2 align-top"><span className="font-regular text-gray-700">{hub.location_code}</span></td>
                        {isSystemAdmin && (
                          <td className="px-5 py-2 align-top"><span className="font-regular text-gray-700 break-words">{hub.company_name || hub.company_id || 'N/A'}</span></td>
                        )}
                        <td className="px-5 py-2 align-top">
                          <div className="font-regular text-gray-700 whitespace-normal break-words max-w-[220px]">
                            {hub.address}
                          </div>
                        </td>
                        <td className="px-5 py-2 align-top">
                          <HubTypeBadges hub={hub} />
                        </td>
                        <td className="px-5 py-2 align-top"><span className="font-regular text-gray-700">{hub.state}</span></td>
                        {showReserve && (
                          <td className="px-5 py-2 align-top"><ReserveLines hub={hub} /></td>
                        )}
                        <td className="px-5 py-2 align-top"><span className="font-regular text-gray-700 break-words">{contactLabel(hub)}</span></td>
                        <td className="px-5 py-2 align-top">
                          <div className="flex items-center">
                            <RowActionsMenu
                              className="relative menu-container"
                              isOpen={openMenuId === hub.id}
                              onToggle={() => setOpenMenuId(openMenuId === hub.id ? null : hub.id)}
                              onClose={() => setOpenMenuId(null)}
                              onView={() => navigate(`/location-hubs/view/${hub.hub_id}`)}
                              onEdit={() => navigate(`/location-hubs/edit/${hub.hub_id}`)}
                              editLabel={t('pages.locationHub.editShort')}
                              onDelete={canDeleteHub ? () => handleDelete(hub.hub_id) : undefined}
                              deleteLabel={t('pages.locationHub.deleteShort')}
                              extraItems={attendanceQrExtraItems(hub)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* iPad portrait: compact scrollable table */}
              <div className="hidden md:block lg:hidden w-full overflow-x-auto">
                <table className="w-full border-collapse min-w-[720px]">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700 border-b">
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider">{t('pages.locationHub.hubBranchColumn', { defaultValue: 'Hub/Branch' })}</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider">Code</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider">Type</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider">State</th>
                      {showReserve && (
                        <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider">Reserve</th>
                      )}
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider">Contact No</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {hubs.map((hub, index) => (
                      <tr key={hub.id} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-3 py-2 align-top">
                          <span
                            className={listIdLinkClass}
                            onClick={() => navigate(`/location-hubs/view/${hub.hub_id}`)}
                          >
                            {hub.location}
                          </span>
                          {isBranchTab && hub.parent_hub_name ? (
                            <p className="text-xs text-gray-500 mt-0.5 break-words">{hub.parent_hub_name}</p>
                          ) : null}
                          <p className="text-xs text-gray-500 mt-0.5 whitespace-normal break-words max-w-[180px]">{hub.address}</p>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700 align-top">{hub.location_code}</td>
                        <td className="px-3 py-2 align-top">
                          <HubTypeBadges hub={hub} />
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700 align-top">{hub.state}</td>
                        {showReserve && (
                          <td className="px-3 py-2 align-top"><ReserveLines hub={hub} /></td>
                        )}
                        <td className="px-3 py-2 text-sm text-gray-700 align-top break-words">{contactLabel(hub)}</td>
                        <td className="px-3 py-2 align-top">
                          <div className="flex items-center">
                            <RowActionsMenu
                              className="relative menu-container"
                              isOpen={openMenuId === hub.id}
                              onToggle={() => setOpenMenuId(openMenuId === hub.id ? null : hub.id)}
                              onClose={() => setOpenMenuId(null)}
                              onView={() => navigate(`/location-hubs/view/${hub.hub_id}`)}
                              onEdit={() => navigate(`/location-hubs/edit/${hub.hub_id}`)}
                              editLabel={t('pages.locationHub.editShort')}
                              onDelete={canDeleteHub ? () => handleDelete(hub.hub_id) : undefined}
                              deleteLabel={t('pages.locationHub.deleteShort')}
                              extraItems={attendanceQrExtraItems(hub)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3 p-3 sm:p-4">
                {hubs.map((hub) => (
                  <div key={hub.id} className="bg-white border rounded-xl p-4 shadow-sm">
                    <div className="flex justify-between items-start mb-3 gap-2">
                      <div className="flex-1 min-w-0">
                        <p className={listIdLinkMobileClass} onClick={() => navigate(`/location-hubs/view/${hub.hub_id}`)}>
                          {hub.hub_id}
                        </p>
                        <p className="text-gray-900 text-base font-semibold mt-1 break-words">{hub.location}</p>
                        {isBranchTab && hub.parent_hub_name ? (
                          <p className="text-xs text-gray-500 mt-0.5 break-words">{hub.parent_hub_name}</p>
                        ) : null}
                        <p className="text-xs text-gray-500 mt-0.5 font-mono">{hub.location_code}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold whitespace-nowrap">{hub.state}</span>
                        <RowActionsMenu
                          isOpen={openMenuId === hub.id}
                          onToggle={() => setOpenMenuId(openMenuId === hub.id ? null : hub.id)}
                          onClose={() => setOpenMenuId(null)}
                          onView={() => navigate(`/location-hubs/view/${hub.hub_id}`)}
                          onEdit={() => navigate(`/location-hubs/edit/${hub.hub_id}`)}
                          editLabel={t('pages.locationHub.editShort')}
                          onDelete={canDeleteHub ? () => handleDelete(hub.hub_id) : undefined}
                          deleteLabel={t('pages.locationHub.deleteShort')}
                          extraItems={attendanceQrExtraItems(hub)}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <HubTypeBadges hub={hub} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                      <div className="col-span-2">
                        <span className="text-gray-500">{t('common.address')}:</span>
                        <p className="text-gray-900 mt-0.5 whitespace-normal break-words">{hub.address}</p>
                      </div>
                      {showReserve && (
                        <div>
                          <span className="text-gray-500">Reserve:</span>
                          <div className="mt-0.5"><ReserveLines hub={hub} /></div>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">Contact No:</span>
                        <p className="text-gray-900 mt-0.5 break-words">{contactLabel(hub)}</p>
                      </div>
                      {isSystemAdmin && (
                        <div>
                          <span className="text-gray-500">{t('common.company')}:</span>
                          <p className="text-gray-900 mt-0.5 break-words">{hub.company_name || hub.company_id || 'N/A'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination — desktop / tablet */}
              <div className="hidden md:flex bg-gray-50 px-4 lg:px-5 py-3 items-center justify-between border-t-2 border-gray-200">
                <span className="text-sm text-gray-600">{showingLabel}</span>
                <ListPagination
          currentPage={currentPage}
          totalPages={totalPages}
          loading={loading && currentPage > 1}
          onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
        />
              </div>

              {/* Pagination — mobile */}
              <div className="md:hidden bg-gray-50 p-4 border-t flex flex-col gap-3">
                <div className="text-center text-sm text-gray-600">
                  {showingLabel} — {t('common.page')} {currentPage} {t('common.of')} {totalPages}
                </div>
                
              </div>
            </>
          )}
        </div>
      </div>

      <BulkExcelModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk Location Hubs"
        description="Download the empty Excel, or use Export for the hubs already saved. Upload either file. Existing hubs are skipped. Extra rows you add are created."
        onDownloadTemplate={async () => {
          const res = await locationHubService.downloadBulkTemplate();
          await downloadBlob(
            res.data,
            'location_hubs_bulk.xlsx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          );
        }}
        onUploadFile={(file) => locationHubService.uploadBulkExcel(file)}
        onImported={() => fetchHubs()}
      />
    </DashboardLayout>
  );
};

export default LocationHubList;
